import React from 'react';
import { useTranslation } from 'react-i18next';
import { SvgPlayFilled } from '../../../icons';
import { Bookmark } from '../../../queries/ats/me/bookmarks/types';
import { BookmarkedSingleRecording } from '../../../queries/ats/recordings/types/recording';
import { Source } from '../../../queries/ats/titles/types/sources';
import { routes } from '../../../routes/route-utils';
import { FocusedAsset } from '../use-focused-asset';
import { appendSequenceNumbers } from '../utils';
import { CallToAction } from './call-to-action-button';
import { getProgress } from './utils';

export interface ContinueWatchingButtonProps {
  focusedAsset: FocusedAsset;
  source: Source;
  bookmark: Bookmark;
}

export function ContinueWatchingButton({ focusedAsset, source, bookmark }: ContinueWatchingButtonProps) {
  const { t } = useTranslation();

  const position = bookmark.isWatched ? 0 : Math.floor(bookmark.position);

  const url = routes.play({
    position,
    platformTitleId: source.platformTitleId,
    providerIdentifier: source.providerIdentifier,
    masterId: focusedAsset.masterId,
  });
  const label = bookmark.isWatched ? t('watch again') : t('details:continue-watching');

  const progress = getProgress(source.bookmark, focusedAsset);

  return (
    <CallToAction
      text={appendSequenceNumbers(label, focusedAsset)}
      image={{
        src: source.image,
        alt: source.name,
      }}
      icon={<SvgPlayFilled />}
      to={url}
      progress={progress}
    />
  );
}

interface RecordedContinueWatchingButtonProps {
  focusedAsset: FocusedAsset;
  recording: BookmarkedSingleRecording;
  image: string;
}

export function RecordedContinueWatchingButton({
  focusedAsset,
  recording,
  image,
}: RecordedContinueWatchingButtonProps) {
  const { t } = useTranslation();

  const position = recording.bookmark.isWatched ? 0 : Math.floor(recording.bookmark.position);
  const url = routes.recording(recording, position);
  const label = recording.bookmark.isWatched ? t('watch again') : t('details:continue-watching');

  const progress = getProgress(recording.bookmark, focusedAsset);

  return (
    <CallToAction
      text={appendSequenceNumbers(label, focusedAsset)}
      image={{
        src: image,
        alt: recording.channelName,
      }}
      icon={<SvgPlayFilled />}
      to={url}
      progress={progress}
    />
  );
}
